// React and Third Party
import { useState } from 'react'
import {
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Row,
  Spinner
} from 'react-bootstrap'
import { ROUTE_LOGIN } from 'components/common/helpers/Paths'
import { isEmail } from 'components/common/helpers/Strings'
import {
  REMBLE_OAUTH_DOMAIN,
  REMBLE_OAUTH_STATE_KEY,
  getOpenIDLoginUrl
} from '..'
import { storeValueWithKey } from 'components/common/helpers/Storage'

/**
 * OAuth flow component, used for OAuth login flows
 */
export default function OAuthFlow({ setIsAuthenticated, history }) {
  const [email, setEmail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isValidEmail, setIsValidEmail] = useState(true) // Default to true, since we autofocus
  const [signupError, setSignupError] = useState('')

  /**
   * Sets and runs validation on `email` field
   * @param {String} value The field's value
   */
  const setAndValidateEmail = value => {
    const isValid = isEmail(value)

    setEmail(value)
    setIsValidEmail(isValid)
  }

  /**
   * Helper function to handle oauth submission
   * @param {Object} data JWT Authentication data
   * @param {String} redirectURL The URL to redirect users to
   */
  const handleSubmit = async event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    // Get domain from email
    const domain = email.split('@')[1]
    const result = await getOpenIDLoginUrl(domain)
    const { url, state } = result
    let err = result.error

    // Store the state in using our storage helper
    storeValueWithKey(REMBLE_OAUTH_STATE_KEY, state)
    storeValueWithKey(REMBLE_OAUTH_DOMAIN, domain)

    // Did we get a valid state?
    if (!state) {
      // Append the error to the signup error
      err += ' No state was returned from the server.'
    }

    // Redirect to the URL
    if (url && state) {
      window.location.href = url
      return
    }

    setIsLoading(false)
    setSignupError(
      err || 'An unknown error occurred and no redirect URL was returned.'
    )
  }

  return (
    <Container>
      <Row>
        <Col>
          <h2 className='subtitle'>Login with OpenID</h2>
          <p>Please provide your email to proceed</p>
          <Form className='signup-form' onSubmit={handleSubmit} noValidate>
            {/* Let's start with email */}
            <Form.Group controlId='formEmail'>
              <FloatingLabel label='Your email address'>
                <Form.Control
                  id='user-email'
                  name='email'
                  disabled={isLoading}
                  size='lg'
                  placeholder='Your email address'
                  required
                  type='email'
                  autoFocus={true}
                  onChange={e => setAndValidateEmail(e.currentTarget.value)}
                  isInvalid={!isValidEmail}
                  isValid={email && isValidEmail}
                  defaultValue={email}
                />
                <Form.Control.Feedback type='invalid'>
                  Please provide a valid email address
                </Form.Control.Feedback>
                <Form.Control.Feedback>
                  Thanks! This will help us connect you to your organization.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
            {/* Did we receive a signup error? */}
            {signupError && (
              <>
                <div className='error'>
                  <i className='icon bi-exclamation-circle'></i>{' '}
                  <strong>Error: </strong>
                  {/* Do we have an array of messages or a string? */}
                  {!Array.isArray(signupError) ? (
                    String(signupError) // Force to string
                  ) : (
                    <>
                      {signupError.map((error, i) => (
                        <span key={i}>{error.msg} </span>
                      ))}
                    </>
                  )}
                </div>
                <Row>
                  <Col>
                    <p className='muted my-5 text-center'>
                      <a href={ROUTE_LOGIN}>
                        <Button variant='secondary'>return to login</Button>
                      </a>
                    </p>
                  </Col>
                </Row>
              </>
            )}
            <Button
              variant='primary'
              size='lg'
              type='submit'
              disabled={isLoading || !isValidEmail || !email}
              className='w-100 mt-5'
            >
              {isLoading ? (
                <>
                  <Spinner
                    as='span'
                    animation='border'
                    size='sm'
                    role='status'
                    aria-hidden='true'
                  />
                  <span className='px-2'>Loading...</span>
                </>
              ) : (
                <span>Continue</span>
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  )
}
